import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Download,
  Headphones,
  Loader2,
  Play,
  RefreshCw,
  Sparkles,
  Volume2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

const SPEEDS = [
  { value: "0.85", label: "0.85× (slow)" },
  { value: "0.95", label: "0.95× (default)" },
  { value: "1.0",  label: "1.0× (normal)" },
  { value: "1.1",  label: "1.1× (brisk)" },
];

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtUSD(n) {
  if (n == null) return "—";
  return `$${Number(n).toFixed(4)}`;
}

/**
 * Provider-aware Voiceover panel (R2-Phase-2.5).
 * Props: runId, narrationReady
 */
export default function VoiceoverPanel({ runId, narrationReady }) {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canGenerate = hasPermission("workflow.run");

  // Catalog
  const providers = useQuery({
    queryKey: ["tts-providers"],
    queryFn: async () => (await api.get("/tts/providers")).data,
    staleTime: 60_000,
  });

  // Selections — initialised from localStorage + populated once catalog loads.
  const [provider, setProvider] = useState(
    localStorage.getItem("eda.voiceover_provider") || "",
  );
  const [voiceId, setVoiceId] = useState(
    localStorage.getItem("eda.voiceover_voice") || "",
  );
  const [speedStr, setSpeedStr] = useState(
    localStorage.getItem("eda.voiceover_speed") || "0.95",
  );

  // On first catalog load, snap to a sensible provider default.
  useEffect(() => {
    if (!providers.data || provider) return;
    const cfg = providers.data.providers.filter((p) => p.configured);
    const def = providers.data.default_provider;
    const chosen =
      cfg.find((p) => p.name === def) || cfg[0] || providers.data.providers[0];
    if (chosen) setProvider(chosen.name);
  }, [providers.data, provider]);

  // Voices for the selected provider
  const voices = useQuery({
    queryKey: ["tts-voices", provider],
    queryFn: async () =>
      (await api.get(`/tts/voices?provider=${provider}`)).data,
    enabled: !!provider,
    staleTime: 60_000,
  });

  // When voices load, auto-pick the provider's default if user hasn't chosen.
  useEffect(() => {
    if (!voices.data?.voices?.length) return;
    const stored = localStorage.getItem(`eda.voiceover_voice__${provider}`);
    if (stored && voices.data.voices.some((v) => v.id === stored)) {
      setVoiceId(stored);
      return;
    }
    const def =
      voices.data.voices.find((v) => v.is_default) || voices.data.voices[0];
    if (def) setVoiceId(def.id);
  }, [voices.data, provider]);

  const providerMeta = useMemo(
    () =>
      providers.data?.providers?.find((p) => p.name === provider) || null,
    [providers.data, provider],
  );
  const supportsSpeed = !!providerMeta?.supports_speed;

  // Existing voiceover for THIS run
  const voiceover = useQuery({
    queryKey: ["voiceover-latest", runId],
    queryFn: async () => {
      try {
        return (await api.get(`/demo-runs/${runId}/voiceover`)).data;
      } catch (e) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: !!runId,
  });

  // Blob URL for in-page audio playback (Bearer auth needed)
  const [blobUrl, setBlobUrl] = useState(null);
  const [blobLoading, setBlobLoading] = useState(false);
  useEffect(() => {
    let active = true;
    let toRevoke = null;
    async function load() {
      if (!voiceover.data?.asset_id) return;
      setBlobLoading(true);
      try {
        const r = await api.get(
          `/demo-runs/${runId}/artifacts/${voiceover.data.asset_id}`,
          { responseType: "blob" },
        );
        if (!active) return;
        const url = window.URL.createObjectURL(r.data);
        toRevoke = url;
        setBlobUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      } catch (err) {
        if (active) toast.error(formatApiError(err));
      } finally {
        if (active) setBlobLoading(false);
      }
    }
    load();
    return () => {
      active = false;
      if (toRevoke) window.URL.revokeObjectURL(toRevoke);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceover.data?.asset_id, runId]);

  // Sample playback (5s preview)
  const [sampleBlobUrl, setSampleBlobUrl] = useState(null);
  useEffect(
    () => () => {
      if (sampleBlobUrl) window.URL.revokeObjectURL(sampleBlobUrl);
    },
    [sampleBlobUrl],
  );

  const sample = useMutation({
    mutationFn: async () => {
      const r = await api.post("/tts/sample", { provider, voice: voiceId });
      const audio = await api.get(
        `/tts/sample/${r.data.provider}/${r.data.voice_id}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(audio.data);
      setSampleBlobUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return url;
      });
      // Auto-play
      setTimeout(() => {
        const el = document.querySelector("[data-testid='voiceover-sample-audio']");
        if (el) {
          el.load();
          el.play().catch(() => {});
        }
      }, 50);
      return r.data;
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const generate = useMutation({
    mutationFn: async ({ regenerate }) => {
      localStorage.setItem("eda.voiceover_provider", provider);
      localStorage.setItem(`eda.voiceover_voice__${provider}`, voiceId);
      localStorage.setItem("eda.voiceover_speed", speedStr);
      const body = {
        provider,
        voice: voiceId,
        regenerate: !!regenerate,
      };
      if (supportsSpeed) body.speed = parseFloat(speedStr);
      const r = await api.post(`/demo-runs/${runId}/generate-voiceover`, body);
      return r.data;
    },
    onSuccess: () => {
      toast.success("Voiceover ready");
      queryClient.invalidateQueries({ queryKey: ["voiceover-latest", runId] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const downloadBlob = useMutation({
    mutationFn: async () => {
      const r = await api.get(
        `/demo-runs/${runId}/artifacts/${voiceover.data.asset_id}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `voiceover_${runId}_v${voiceover.data.narration_version}.mp3`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  // Current-month cost telemetry for the chosen provider
  const monthUsage = providerMeta?.month_usage;

  return (
    <section
      className="mt-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3"
      data-testid="voiceover-panel"
    >
      <header className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <Headphones size={14} className="text-indigo-600" />
          Voiceover
        </div>
        {monthUsage && (
          <span className="text-[10px] text-slate-400">
            {providerMeta.name} this month: {monthUsage.calls} calls ·{" "}
            {fmtUSD(monthUsage.est_cost_usd)}
          </span>
        )}
      </header>

      {/* Provider + voice selectors */}
      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-12">
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger
            className="sm:col-span-4 h-8 text-xs"
            data-testid="voiceover-provider-select"
          >
            <SelectValue placeholder="Provider" />
          </SelectTrigger>
          <SelectContent>
            {(providers.data?.providers || []).map((p) => (
              <SelectItem
                key={p.name}
                value={p.name}
                disabled={!p.configured}
                data-testid={`voiceover-provider-opt-${p.name}`}
              >
                {p.label}{" "}
                <span className="text-[10px] text-slate-400">
                  {p.configured ? "" : "(not configured)"}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={voiceId}
          onValueChange={setVoiceId}
          disabled={!voices.data?.voices?.length}
        >
          <SelectTrigger
            className="sm:col-span-5 h-8 text-xs"
            data-testid="voiceover-voice-select"
          >
            <SelectValue placeholder="Voice" />
          </SelectTrigger>
          <SelectContent>
            {(voices.data?.voices || []).map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.name}{" "}
                {v.is_default ? (
                  <span className="text-[10px] text-indigo-600">(default)</span>
                ) : null}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={speedStr}
          onValueChange={setSpeedStr}
          disabled={!supportsSpeed}
        >
          <SelectTrigger
            className="sm:col-span-2 h-8 text-xs"
            data-testid="voiceover-speed-select"
            title={
              supportsSpeed
                ? "Playback speed"
                : "Speed not supported by this provider"
            }
          >
            <SelectValue placeholder="Speed" />
          </SelectTrigger>
          <SelectContent>
            {SPEEDS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          size="sm"
          variant="outline"
          onClick={() => sample.mutate()}
          disabled={!voiceId || sample.isPending}
          className="sm:col-span-1 h-8 px-2"
          title="Generate a 5-second voice sample"
          data-testid="voiceover-sample-btn"
        >
          {sample.isPending ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Play size={13} />
          )}
        </Button>
      </div>

      {sampleBlobUrl && (
        <audio
          controls
          src={sampleBlobUrl}
          className="mb-3 w-full"
          data-testid="voiceover-sample-audio"
        />
      )}

      {voiceover.isLoading ? (
        <div
          className="flex items-center gap-2 py-2 text-xs text-slate-500"
          data-testid="voiceover-loading"
        >
          <Loader2 size={13} className="animate-spin" /> Loading voiceover…
        </div>
      ) : !voiceover.data ? (
        <div className="space-y-2" data-testid="voiceover-empty">
          <p className="text-xs text-slate-500">
            No voiceover yet for this run. Pick a voice and click Generate.
          </p>
          <Button
            size="sm"
            onClick={() => generate.mutate({ regenerate: false })}
            disabled={
              !canGenerate ||
              !narrationReady ||
              !voiceId ||
              generate.isPending
            }
            className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300"
            data-testid="voiceover-generate"
          >
            {generate.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Volume2 size={13} />
            )}
            Generate Voiceover
          </Button>
          {!narrationReady && (
            <p className="text-[11px] text-slate-400">
              Generate the narration script first.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="voiceover-ready">
          {voiceover.data.stale && (
            <div
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800"
              data-testid="voiceover-stale-banner"
            >
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>
                Voiceover is out of date — the narration has been updated
                since this audio was generated. Regenerate to refresh.
              </span>
            </div>
          )}
          {blobLoading ? (
            <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
              <Loader2 size={13} className="animate-spin" /> Loading audio…
            </div>
          ) : blobUrl ? (
            <audio
              controls
              src={blobUrl}
              preload="metadata"
              className="w-full"
              data-testid="voiceover-audio"
            >
              Your browser doesn&apos;t support audio playback.
            </audio>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-500">
            <span data-testid="voiceover-meta">
              {voiceover.data.provider} ·{" "}
              {voiceover.data.voice_label || voiceover.data.voice_id} ·{" "}
              {voiceover.data.speed}× · {voiceover.data.model} ·{" "}
              {fmtBytes(voiceover.data.size_bytes)} · narration v
              {voiceover.data.narration_version}
              {voiceover.data.est_cost_usd != null && (
                <> · {fmtUSD(voiceover.data.est_cost_usd)}</>
              )}
              {voiceover.data.tts_elapsed_ms != null && (
                <>
                  {" "}· generated in{" "}
                  {Math.round(voiceover.data.tts_elapsed_ms / 100) / 10}s
                </>
              )}
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[11px]"
                onClick={() => downloadBlob.mutate()}
                disabled={downloadBlob.isPending}
                data-testid="voiceover-download"
              >
                {downloadBlob.isPending ? (
                  <Loader2 size={11} className="mr-1 animate-spin" />
                ) : (
                  <Download size={11} className="mr-1" />
                )}
                Download MP3
              </Button>
              {canGenerate && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={() => generate.mutate({ regenerate: true })}
                  disabled={generate.isPending}
                  data-testid="voiceover-regenerate"
                >
                  {generate.isPending ? (
                    <Loader2 size={11} className="mr-1 animate-spin" />
                  ) : (
                    <Sparkles size={11} className="mr-1" />
                  )}
                  Regenerate
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
